import React, {
  useState,
  useLayoutEffect,
  useRef,
  useEffect,
  useCallback,
} from "react";
import debounce from "lodash/debounce";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import LogoutButton from "./LogoutButton";
import ImageFile from "../images/afrogebeya.png";
import "../styles/header.css";

function Header({ hideExtras = false }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const hideTimerRef = useRef(null);
  const searchWrapperRef = useRef(null);

  const { auth, isAuthenticated } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const displayName = auth?.firstName ?? "User";
  const role = auth?.role ?? "buyer"; // enterprise: role-based menus

  const pathSegment = location.pathname.split("/")[1]?.toLowerCase();
  const prefix = pathSegment === "seller" ? "/seller" : "/buyer";
  const hideExtrasOnPath =
    location.pathname.startsWith("/seller") ||
    location.pathname.startsWith("/admin");

  const fetchSuggestions = useCallback(
    debounce(async (value) => {
      if (!value.trim()) {
        setSuggestions([]);
        return;
      }
      try {
        const res = await fetch(
          `${process.env.REACT_APP_PRODUCT_SERVICE_URL}/products/search/suggestions?q=${value}`,
        );
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data);
        }
      } catch (err) {
        console.error("Suggestion error:", err);
      }
    }, 300),
    [],
  );

  useEffect(() => {
    fetchSuggestions(searchTerm);
  }, [searchTerm, fetchSuggestions]);

  /* ---------- FIX CONTENT OFFSET ---------- */
  useLayoutEffect(() => {
    const header = document.querySelector(".navbar-custom");
    const main = document.querySelector("#main-content");
    if (header && main) {
      main.style.paddingTop = `${header.offsetHeight}px`;
    }
  }, []);

  /* ---------- SEARCH ---------- */
  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    navigate(`/search?q=${searchTerm}`);
    setSuggestions([]);
  };

  /* ---------- DROPDOWN HELPERS ---------- */
  const closeMenu = () => {
    clearTimeout(hideTimerRef.current);
    setShowUserMenu(false);
  };

  const startAutoClose = () => {
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(closeMenu, 3000);
  };

  /* ---------- CLICK OUTSIDE ---------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchWrapperRef.current &&
        !searchWrapperRef.current.contains(e.target)
      ) {
        setSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------- ESC KEY ---------- */
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  return (
    <nav className="navbar navbar-dark navbar-custom fixed-top shadow-sm px-3">
      <div className="container-fluid d-flex align-items-center gap-3">
        {/* Logo */}
        <Link to="/" className="navbar-brand d-flex align-items-center gap-2">
          <img
            src={ImageFile}
            alt="Afrogebeya Logo"
            height="38"
            loading="lazy"
          />
          <strong>Afrogebeya</strong>
        </Link>

        {/* Search */}
        {!hideExtras && !hideExtrasOnPath && (
          <div
            ref={searchWrapperRef}
            className="search-wrapper flex-grow-1 mx-2 mx-md-4"
          >
            <form
              onSubmit={handleSearch}
              className="d-flex w-100 position-relative"
            >
              <i className="bi bi-search search-icon"></i>
              <input
                className="form-control form-control-sm"
                type="search"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                aria-label="Search products"
              />
              <button className="btn btn-info btn-sm ms-2 px-3 d-none d-sm-inline">
                Search
              </button>
              <button className="btn btn-primary btn-sm ms-2 d-sm-none">
                🔍
              </button>
            </form>
            {suggestions.length > 0 && (
              <div className="card shadow suggestion-dropdown">
                {suggestions.map((item) => (
                  <div
                    key={item.id}
                    className="suggestion-item"
                    role="option"
                    onClick={() => {
                      navigate(`/products/${item.id}`);
                      setSuggestions([]);
                      setSearchTerm("");
                    }}
                  >
                    <div className="fw-semibold">{item.productName}</div>
                    <div className="small text-muted">
                      {item.price} ETB • {item.condition}
                    </div>
                  </div>
                ))}
                <div
                  className="suggestion-view-all"
                  onClick={() => {
                    navigate(`/search?q=${searchTerm}`);
                    setSuggestions([]);
                  }}
                >
                  View all results →
                </div>
              </div>
            )}
          </div>
        )}

        {/* Right Actions */}
        {!hideExtras && (
          <div className="d-flex align-items-center gap-3 ms-auto">
            {/* Cart */}
            <Link
              to="/cart"
              className="btn btn-outline-light position-relative cart-btn"
              aria-label={`Cart with ${cartCount} items`}
            >
              <i className="bi bi-cart4"></i>
              {cartCount > 0 && (
                <span className="badge bg-danger position-absolute top-0 start-100 translate-middle">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* User Menu */}
            {isAuthenticated ? (
              <div className="position-relative">
                <button
                  ref={buttonRef}
                  className="btn btn-outline-light btn-sm d-flex align-items-center gap-1"
                  onClick={() => {
                    setShowUserMenu((v) => !v);
                    startAutoClose();
                  }}
                  aria-haspopup="true"
                  aria-expanded={showUserMenu}
                  aria-controls="user-menu"
                >
                  <img
                    src={auth?.avatarUrl ?? "/default-avatar.png"}
                    alt="User Avatar"
                    className="rounded-circle"
                    width="24"
                    height="24"
                  />
                  <span className="d-none d-md-inline">{displayName}</span>
                  <i className="bi bi-caret-down-fill small"></i>
                </button>

                <div
                  id="user-menu"
                  ref={dropdownRef}
                  className={`user-dropdown ${showUserMenu ? "open" : ""}`}
                  role="menu"
                  onMouseEnter={() => clearTimeout(hideTimerRef.current)}
                  onMouseLeave={startAutoClose}
                >
                  <Link
                    role="menuitem"
                    to={`${prefix}/notifications`}
                    onClick={closeMenu}
                  >
                    🔔 Notifications
                  </Link>
                  <hr />
                  <Link
                    role="menuitem"
                    to={`${prefix}/profile`}
                    onClick={closeMenu}
                  >
                    👤 Profile
                  </Link>
                  {role === "seller" && (
                    <Link
                      role="menuitem"
                      to={`${prefix}/my-shop`}
                      onClick={closeMenu}
                    >
                      🏪 My Shop
                    </Link>
                  )}
                  {role === "admin" && (
                    <Link
                      role="menuitem"
                      to="/admin/dashboard"
                      onClick={closeMenu}
                    >
                      🛠 Admin Dashboard
                    </Link>
                  )}
                  <Link
                    role="menuitem"
                    to={`${prefix}/settings`}
                    onClick={closeMenu}
                  >
                    ⚙️ Settings
                  </Link>
                  <hr />
                  <LogoutButton closeMenu={closeMenu} />
                </div>
              </div>
            ) : (
              <>
                <Link to="/login" className="btn btn-outline-light btn-sm">
                  Login
                </Link>
                <Link to="/register" className="btn btn-outline-light btn-sm">
                  Register
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}

export default Header;
